import { gsap } from "gsap";
import InteractionManager from "./InteractionManager.js";
import RevealAnimationManager from "./RevealAnimationManager.js";
import SceneManager from "./SceneManager.js";
import Wheel from "./Wheel.js";

const SPINNING_SPEED = 0.6; // Constant speed when in spinning state (spins per second)
const ERROR_SPIN_SPEED = 0.4; // Has to be slower than the minimum velocity for spinning
const BUTTON_CLICK_SPIN_SPEED = 2;

/**
 * Check if user prefers reduced motion
 * @returns {boolean}
 */
const prefersReducedMotion = () => {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

class SpinToWin {
  state = "ready";
  /* States:
  1. ready - the wheel is ready to spin and is interactable
  2. spinning - the wheel is automatically spinning and waiting for the result
  3. animatingToResult - the result is in and the wheel is animating to the winning position
  4. revealingResult - the winning result is being displayed and animated
  5. complete - the wheel can no longer be spun and is grayed out
  */
  sceneManager = null;
  wheel = null;
  interactionManager = null;
  revealAnimationManager = null;
  fetchSpinToWinResults = null;
  winningCoin = null;
  buttonEl = null;
  skipButtonEl = null;
  sectionEl = null;
  textContainerEl = null;
  srAnnouncementEl = null; // Screen reader announcement element
  tickerListener = null; // GSAP ticker listener function
  canvasSizeNeedsUpdate = false; // Flag to indicate canvas size needs updating
  orbitContentRect = null; // Bounding box of the visible content inside the center hole
  showToast = null;
  prefersReducedMotion = false; // Flag to track if user prefers reduced motion
  isComplete = false;
  isAuthenticated = false;
  loginUrl = null;
  pointsMessageSingular = null;
  pointsMessagePlural = null;

  constructor({canvas, sectionEl, fetchSpinToWinResults, buttonEl, showToast, isComplete, isAuthenticated, loginUrl, pointsMessageSingular, pointsMessagePlural}) {
    this.prefersReducedMotion = prefersReducedMotion();
    this.fetchSpinToWinResults = fetchSpinToWinResults;
    this.buttonEl = buttonEl;
    this.sectionEl = sectionEl;
    this.skipButtonEl = sectionEl?.querySelector('.spin-to-win__skip-button');
    this.textContainerEl = sectionEl?.querySelector('.spin-to-win__text-container');
    this.srAnnouncementEl = sectionEl?.querySelector('.spin-to-win__sr-announcement');
    this.showToast = showToast;
    // Prefer explicit prop, fall back to data attribute on section.
    const explicitIsComplete = typeof isComplete === 'boolean' ? isComplete : null;
    const isCompleteFromDom = sectionEl?.dataset?.isComplete === 'true';
    this.isComplete = explicitIsComplete ?? isCompleteFromDom;
    // Prefer explicit prop, fall back to data attribute on section.
    const explicitIsAuthenticated = typeof isAuthenticated === 'boolean' ? isAuthenticated : null;
    const isAuthenticatedFromDom = sectionEl?.dataset?.isAuthenticated === 'true';
    this.isAuthenticated = explicitIsAuthenticated ?? isAuthenticatedFromDom;
    // Prefer explicit prop, fall back to data attribute on section.
    const explicitLoginUrl = loginUrl || null;
    const loginUrlFromDom = sectionEl?.dataset?.loginUrl || null;
    this.loginUrl = explicitLoginUrl ?? loginUrlFromDom;
    // Set points messages from props (passed from drupalSettings).
    this.pointsMessageSingular = pointsMessageSingular;
    this.pointsMessagePlural = pointsMessagePlural;
    this.sceneManager = new SceneManager(canvas, this.prefersReducedMotion);
    this.revealAnimationManager = new RevealAnimationManager({
      onComplete: this.handleRevealAnimationComplete.bind(this),
      onBlurUpdate: this.handleBlurUpdate.bind(this),
      onResultRevealed: this.handleResultRevealed.bind(this),
      prefersReducedMotion: this.prefersReducedMotion,
    });
    this.setState(this.isComplete ? "complete" : "ready");
    this.wheel = new Wheel({
      onShouldStartSpinning: this.handleShouldStartSpinning.bind(this),
      onShouldStopAtResult: this.handleShouldStopAtResult.bind(this),
      prefersReducedMotion: this.prefersReducedMotion,
    });
    this.interactionManager = new InteractionManager({
      canvas,
      camera: this.sceneManager.camera,
      coins: this.sceneManager.coins,
      sectionEl: this.sectionEl,
      buttonEl: this.buttonEl,
      sceneManager: this.sceneManager,
      prefersReducedMotion: this.prefersReducedMotion,
    });
  }

  handleShouldStartSpinning(isButtonClick = false) {
    if (this.state === "ready") {
      // Check if user is authenticated before starting spin
      if (!this.isAuthenticated && this.loginUrl) {
        window.location.href = this.loginUrl;
        return;
      }

      if (this.prefersReducedMotion) {
        // Skip spinning animation, go directly to fetching results
        this.setState("spinning");
        this.fetchResults();
      } else {
        if(isButtonClick) {
          this.wheel.setTargetSpinSpeed(BUTTON_CLICK_SPIN_SPEED);
          this.setState("spinning");
        }
        setTimeout(() => {
          const currentSpinSpeed = this.wheel.getSpinSpeed()
          this.setState("spinning");
          // Set target speed to constant spinning speed, preserving direction
          const targetSpinSpeed = currentSpinSpeed >= 0 ? SPINNING_SPEED : -SPINNING_SPEED;
          this.wheel.setTargetSpinSpeed(targetSpinSpeed);
          this.fetchResults();
        // If this was a button click wait for the wheel to spin up a bit
        }, isButtonClick ? 600 : 0);
      }
    }
  }

  handleShouldStopAtResult() {
    if (this.state === "animatingToResult") {
      this.setState("revealingResult");
      // Start the reveal animation immediately when state changes to revealingResult
      if (this.winningCoin) {
        this.sceneManager.setWinningCoin(this.winningCoin);
        this.revealAnimationManager.startRevealAnimation({
          coin: this.winningCoin,
          camera: this.sceneManager.camera,
          scene: this.sceneManager.scene,
        });
      }
    }
  }

  handleRevealAnimationComplete() {
    this.revealAnimationManager.stopAnimation();
    this.setState("complete");
  }

  /**
   * Update screen reader announcement
   * @param {string} message - The message to announce
   */
  updateScreenReaderAnnouncement(message) {
    if (this.srAnnouncementEl) {
      // Clear previous announcement first
      this.srAnnouncementEl.setAttribute("aria-hidden", "true");
      this.srAnnouncementEl.textContent = "";

      // Use requestAnimationFrame to ensure the DOM update is processed
      // before making the element visible again
      requestAnimationFrame(() => {
        if (this.srAnnouncementEl && message) {
          this.srAnnouncementEl.setAttribute("aria-hidden", "false");
          this.srAnnouncementEl.textContent = message;
        }
      });
    }
  }

  handleResultRevealed() {
    // Trigger coins toast when coin reaches center
    const points = parseInt(this.winningCoin.label, 10) || 1;
    // Use config-based message template with Drupal.t() for proper translation
    const messageTemplate = points === 1
      ? this.pointsMessageSingular
      : this.pointsMessagePlural;
    const message = Drupal.t(messageTemplate, { '@points': points });

    Drupal.toast(Drupal.t("Congratulations!"), "coins", {
      additional_props: {
        points: points,
        description: message,
      },
    });

    // Announce the number of coins won to screen readers using config message
    if (this.winningCoin) {
      const coinLabel = this.winningCoin.label;
      const coinCount = parseInt(coinLabel, 10);
      let announcement = "";
      if (!isNaN(coinCount)) {
        const srTemplate = coinCount === 1
          ? this.pointsMessageSingular
          : this.pointsMessagePlural;
        announcement = Drupal.t(srTemplate, { '@points': coinCount });
      } else {
        announcement = Drupal.t(this.pointsMessagePlural, { '@points': coinLabel });
      }

      this.updateScreenReaderAnnouncement(announcement);
    }
  }

  handleSkipAnimation() {
    if (this.state === "revealingResult") {
      this.revealAnimationManager.stopAnimation();
      this.setState("complete");
    }
  }

  /**
   * Keep skip button out of keyboard flow unless it is visibly available.
   */
  updateSkipButtonAccessibility(isVisible) {
    if (!this.skipButtonEl) {
      return;
    }

    if (isVisible) {
      this.skipButtonEl.removeAttribute("hidden");
      this.skipButtonEl.removeAttribute("tabindex");
      this.skipButtonEl.removeAttribute("disabled");
      return;
    }

    if (document.activeElement === this.skipButtonEl) {
      if (this.buttonEl && typeof this.buttonEl.focus === "function") {
        this.buttonEl.focus();
      } else if (this.sectionEl && typeof this.sectionEl.focus === "function") {
        this.sectionEl.focus();
      } else {
        this.skipButtonEl.blur();
      }
    }

    this.skipButtonEl.setAttribute("hidden", "hidden");
    this.skipButtonEl.setAttribute("tabindex", "-1");
    this.skipButtonEl.setAttribute("disabled", "disabled");
  }

  fetchResults() {
    const fetchPromise = this.fetchSpinToWinResults?.(this.sceneManager?.coins)
      .catch((error) => {
        // Catch errors so Promise.all doesn't reject early
        // Return error object to handle after timeout
        return { type: 'error', error };
      });
    const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 3000));

    Promise.all([fetchPromise, timeoutPromise])
      .then(([result]) => {
        // Handle fetch errors after timeout completes
        if (result && typeof result === 'object' && result.type === 'error') {
          console.error('SpinToWin: Error fetching results', result.error);
          this.wheel.setSpinSpeed(ERROR_SPIN_SPEED);
          this.wheel.setTargetSpinSpeed?.(0);
          this.setState("ready");
          return;
        }

        // Handle structured result from fetchSpinToWinResults.
        if (result && typeof result === 'object') {
          if (result.type === 'limit') {
            // Daily limit reached – stop spinning and mark as complete.
            this.wheel.setSpinSpeed(ERROR_SPIN_SPEED);
            this.wheel.setTargetSpinSpeed?.(0);
            this.setState("complete");
            return;
          }

          if (result.type === 'success') {
            result = result.points;
          }
        }

        if (typeof result !== 'number') {
          console.error('SpinToWin: Invalid result from fetchSpinToWinResults', result);
          this.wheel.setSpinSpeed(ERROR_SPIN_SPEED);
          this.wheel.setTargetSpinSpeed?.(0);
          this.setState("ready");
          return;
        }

        // Find the coin with the winning label matching the number of coins
        let winningCoin = this.sceneManager?.coins?.find(
          (coin) => coin.label === String(result),
        );

        if (!winningCoin) {
          winningCoin = this.sceneManager?.coins?.[0];
        }

        // Store the winning coin for reveal animation
        this.winningCoin = winningCoin;

        // Calculate target rotation to align winning coin to bottom (-π/2)
        // The bottom position is at -π/2 (or 3π/2)
        // Coin's orbital rotation = wheelRotation + (coin.index * rotationPerCoin)
        // We want: wheelRotation + (coin.index * rotationPerCoin) = -π/2 (mod 2π)
        const rotationPerCoin = (Math.PI * 2) / winningCoin.totalCoins;
        const currentWheelRotation = this.wheel.getRotation();
        const currentCoinRotation = currentWheelRotation + (winningCoin.index * rotationPerCoin);

        // Calculate how much we need to rotate to get to -π/2
        let targetCoinRotation = -Math.PI / 2;
        let rotationDiff = targetCoinRotation - currentCoinRotation;

        // Normalize to [-π, π] range and add full rotations for smooth spin
        while (rotationDiff > Math.PI) rotationDiff -= 2 * Math.PI;
        while (rotationDiff < -Math.PI) rotationDiff += 2 * Math.PI;

        if (this.prefersReducedMotion) {
          // Skip animation, set wheel directly to target rotation
          const targetWheelRotation = currentWheelRotation + rotationDiff;
          this.wheel.setRotation(targetWheelRotation);
          this.wheel.setSpinSpeed(0);
          // Skip animatingToResult state and go directly to revealingResult
          this.setState("revealingResult");
          // Set winning coin and start reveal (which will be instant)
          if (this.winningCoin) {
            this.sceneManager.setWinningCoin(this.winningCoin);
            this.revealAnimationManager.startRevealAnimation({
              coin: this.winningCoin,
              camera: this.sceneManager.camera,
              scene: this.sceneManager.scene,
            });
          }
        } else {
          // Add a few extra rotations for visual effect (e.g., 2-3 full rotations)
          const extraRotations = 2;
          rotationDiff += extraRotations * 2 * Math.PI;

          const targetWheelRotation = currentWheelRotation + rotationDiff;
          this.wheel.setTargetRotation(targetWheelRotation);
          // Set state to animatingToResult when result is returned
          this.setState("animatingToResult");
        }
      });
  }

  setState(state) {
    const previousState = this.state;
    this.state = state;
    if (this.sectionEl) {
      this.sectionEl.setAttribute("data-state", state);
    }

    // Update screen reader announcements based on state
    if (state === "spinning") {
      this.updateScreenReaderAnnouncement("Loading spin result...");
    } else if (state === "ready" || state === "complete") {
      // Clear announcement when returning to ready or reaching complete
      this.updateScreenReaderAnnouncement("");
    }

    // Update aria-hidden attributes for screen reader announcements
    if (this.textContainerEl) {
      const defaultTitleEl = this.textContainerEl.querySelector('.spin-to-win__title--default');
      const defaultDescriptionEl = this.textContainerEl.querySelector('.spin-to-win__description--default');
      const completeStateEl = this.textContainerEl.querySelector('.spin-to-win__complete-state');

      if (state === "complete") {
        // Hide default state from screen readers
        if (defaultTitleEl) {
          defaultTitleEl.setAttribute("aria-hidden", "true");
        }
        if (defaultDescriptionEl) {
          defaultDescriptionEl.setAttribute("aria-hidden", "true");
        }
        // Show complete state to screen readers
        if (completeStateEl) {
          completeStateEl.setAttribute("aria-hidden", "false");
        }
      } else {
        // Show default state to screen readers
        if (defaultTitleEl) {
          defaultTitleEl.setAttribute("aria-hidden", "false");
        }
        if (defaultDescriptionEl) {
          defaultDescriptionEl.setAttribute("aria-hidden", "false");
        }
        // Hide complete state from screen readers
        if (completeStateEl) {
          completeStateEl.setAttribute("aria-hidden", "true");
        }
      }
    }

    requestAnimationFrame(() => {
      this.measureOrbitContentRect();
    });

    this.updateSkipButtonAccessibility(state === "revealingResult");

    // Clean up blur when leaving revealingResult state
    if (previousState === "revealingResult" && state !== "revealingResult") {
      this.sceneManager.setWinningCoin(null);
      // Blur will be reset by GSAP timeline revert
    }
  }

  handleBlurUpdate(blurAmount) {
    // Update scene manager blur
    this.sceneManager.setBlurAmount(blurAmount);
    // Update text blur
    this.updateTextBlur(blurAmount);
  }

  updateTextBlur(blurAmount) {
    if (this.textContainerEl) {
      // Map blur amount (0-1) to CSS blur (0-8px)
      // Manually set without CSS transitions
      const cssBlur = blurAmount * 8;
      this.textContainerEl.style.filter = `blur(${cssBlur}px)`;
      // Remove transition to manually control the animation
      this.textContainerEl.style.transition = 'none';
    }
  }

  measureOrbitContentRect() {
    if (!this.textContainerEl) {
      this.orbitContentRect = null;
      return;
    }

    const measuredNodes = this.textContainerEl.querySelectorAll(
      ".spin-to-win__title, .spin-to-win__description, .spin-to-win__button, .spin-to-win__complete-icon",
    );

    let minLeft = Number.POSITIVE_INFINITY;
    let minTop = Number.POSITIVE_INFINITY;
    let maxRight = Number.NEGATIVE_INFINITY;
    let maxBottom = Number.NEGATIVE_INFINITY;

    measuredNodes.forEach((node) => {
      const style = window.getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden") {
        return;
      }

      const rect = node.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      minLeft = Math.min(minLeft, rect.left);
      minTop = Math.min(minTop, rect.top);
      maxRight = Math.max(maxRight, rect.right);
      maxBottom = Math.max(maxBottom, rect.bottom);
    });

    if (!Number.isFinite(minLeft)) {
      this.orbitContentRect = null;
      return;
    }

    this.orbitContentRect = {
      width: maxRight - minLeft,
      height: maxBottom - minTop,
    };
  }

  getState() {
    return this.state;
  }

  /**
   * Mark that canvas size needs to be updated
   * This prevents race conditions by updating in the animation loop
   */
  markCanvasSizeNeedsUpdate() {
    this.canvasSizeNeedsUpdate = true;
  }

  /**
   * Animation loop using GSAP ticker
   * Runs after GSAP's global timeline updates, sharing the same RAF
   * @param {number} time - Total time in seconds since ticker started
   * @param {number} deltaTime - Milliseconds elapsed since previous tick
   * @param {number} frame - Frame number
   */
  animate(time, deltaTime, frame) {
    // Update canvas size at the start of animation loop to prevent race conditions
    if (this.canvasSizeNeedsUpdate) {
      this.sceneManager.updateCanvasSize();
      this.interactionManager.updateCanvasRect();
      this.measureOrbitContentRect();
      this.canvasSizeNeedsUpdate = false;
    }

    // Convert deltaTime from milliseconds to seconds for wheel update
    const timeDelta = deltaTime;

    const cursorState = this.interactionManager.getCursorState();
    this.wheel.update(timeDelta, cursorState, this.state);

    // Update cursor state based on component state (only for non-ready states)
    // For ready state, cursor is managed by mousemove/pointerdown handlers
    if (this.state !== "ready") {
      this.interactionManager.updateCursorState(this.state);
    }

    const wheelRotation = this.wheel.getRotation();
    const spinsPerSecond = this.wheel.getSpinSpeed();
    const mousePos = this.interactionManager.getMousePos();
    this.sceneManager.updateCoins({
      wheelRotation,
      mousePos,
      timeDelta,
      state: this.state,
      revealAnimationManager: this.revealAnimationManager,
      spinsPerSecond,
      contentRect: this.orbitContentRect,
    });
    // Update rays animation
    this.sceneManager.updateRays(timeDelta);
    this.sceneManager.render();
  }

  /**
   * Clean up and remove ticker listener
   */
  destroy() {
    if (this.tickerListener) {
      gsap.ticker.remove(this.tickerListener);
      this.tickerListener = null;
    }
  }

  async init() {
    await this.sceneManager.setupScene();
    this.interactionManager.setupEventListeners(this.sceneManager, this);

    // Set up skip button event listener
    if (this.skipButtonEl) {
      this.skipButtonEl.addEventListener("click", () => {
        this.handleSkipAnimation();
      });
    }

    // Use GSAP ticker to share RAF with GSAP timeline
    // The ticker runs after GSAP's global timeline updates
    this.tickerListener = this.animate.bind(this);
    gsap.ticker.add(this.tickerListener);

    // Mark canvas size for update after CSS layout is complete
    // This is especially important for viewport units (100vw x 100lvh)
    requestAnimationFrame(() => {
      this.markCanvasSizeNeedsUpdate();
      this.measureOrbitContentRect();
    });
  }
}

export default SpinToWin;
